import { useAuthContext } from "@context-providers/auth/auth-context";
import {
  IconButton,
  Link,
  UserAvatar,
  UserMenu as UserMenuDropdown,
  UserAvatarIcon,
} from "@flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useRouteMatch } from "@hooks/use-route-match";
import { useRedirectToAccountSettingsPage } from "@pages/account-settings/account-settings-utils";
import { AccountSettingsTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { runtimeConfig } from "@src/runtime-config";
import { useAppSelector } from "@store/store-helper";
import {
  currentUserSelector,
  loggedInUserSelector,
} from "@store/user/user-selector";
import { UserMenuEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  getPendingUserIcon,
  getPrettyName,
  getUserInitials,
} from "@utils/user-utils";
import { useMemo, useState } from "react";
/**
 * Gets a component that show a user menu to be inserted in the header bar.
 *
 * @returns React user menu component.
 */
export function UserMenu(): JSX.Element {
  const { trackEvent, trackAsyncEvent } = useTrackEvent();
  const routeIdentifier = useRouteMatch();
  const { accountSettingsTab } = useAppParams();
  const currentUser = useAppSelector(currentUserSelector);
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const { logout } = useAuthContext();
  const { navigateToRoot, navigateToAccountSettingsPage } = useAppNavigation();
  const redirectToAccountSettingsPage = useRedirectToAccountSettingsPage();

  /** HTML element used to set the position of the menu. */
  const [menuAnchorElement, setMenuAnchorElement] = useState<
    HTMLElement | undefined
  >(undefined);
  const isMenuOpen = Boolean(menuAnchorElement);

  /** Links used at the bottom of the user menu component */
  const links: Link[] = useMemo(() => {
    return [
      {
        text: "Terms of service",
        href: `${runtimeConfig.urls.sphereEntryPageUrl}/legal/#terms-of-service`,
        callback: () => {
          trackEvent({ name: UserMenuEvents.openTermsDetails });
        },
      },
      {
        text: "Privacy policy",
        href: `${runtimeConfig.urls.sphereEntryPageUrl}/legal/#privacy-policy`,
        callback: () => {
          trackEvent({ name: UserMenuEvents.openPrivacyDetails });
        },
      },
      {
        text: "Imprint",
        href: "https://www.faro.com/de-DE/Imprint",
        callback: () => {
          trackEvent({ name: UserMenuEvents.openImprintDetails });
        },
      },
    ];
  }, [trackEvent]);

  /**
   * Gets the user information to be displayed in the user menu.
   * Depending on the available information about the user it will use different sources.
   * If the current user is available it will use that information, otherwise it will use
   * the logged in user information.
   */
  const userData = useMemo(() => {
    // Prefer current user over logged in user since it is more complete.
    if (currentUser) {
      return {
        id: currentUser.id,
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        fullName: getPrettyName(currentUser),
        userInitials: getUserInitials(currentUser),
        thumbnailUrl: currentUser.thumbnailUrl,
        icon: getPendingUserIcon(currentUser),
        largeIcon: getPendingUserIcon(currentUser, "large"),
      };
    }

    // Logged in user is the only information available on page to select workspaces,
    // since the route to fetch user details require a company ID.
    if (loggedInUser) {
      const { id, firstName, lastName, email, thumbnailUrl } = loggedInUser;
      return {
        id,
        email,
        firstName,
        lastName,
        fullName: getPrettyName({
          firstName,
          lastName,
        }),
        userInitials: getUserInitials({
          // For the logged in user route the firstName is under the name property.
          firstName,
          lastName,
        }),
        thumbnailUrl,
        icon: undefined,
        largeIcon: undefined,
      };
    }
    return undefined;
  }, [currentUser, loggedInUser]);

  /** Gets the user information for userMenu from flat-ui */
  const userDisplayInfo = useMemo(() => {
    if (!userData) {
      return undefined;
    }

    return {
      email: userData.email,
      firstName: userData.firstName ?? "",
      lastName: userData.lastName ?? "",
      name: userData.fullName,
      profileImageUrl: userData.thumbnailUrl,
    };
  }, [userData]);

  /** Triggered when the button is clicked to open the menu */
  function handleClick(event: React.MouseEvent<HTMLElement>): void {
    trackEvent({ name: UserMenuEvents.openUserMenu });

    setMenuAnchorElement(event.currentTarget);
  }

  /** Closes the user menu */
  function handleClose(): void {
    setMenuAnchorElement(undefined);
  }

  function handleAccountSettingsClick(tab: AccountSettingsTabs): void {
    // If the user is already in the settings tab that they clicked then avoid any further action.
    // Otherwise if user clicked on a different tab of settings page then navigate within the single page app.
    if (routeIdentifier === "account") {
      if (tab === accountSettingsTab) {
        handleClose();
        return;
      } else {
        trackEvent({
          name: UserMenuEvents.openAccountSettings,
          props: { from: "user-menu dropdown" },
        });
        handleClose();
        navigateToAccountSettingsPage(tab);
        return;
      }
    }

    // If user click on the settings page from any other route other than "account" then perform a navigation
    // outside the single page app.
    void trackAsyncEvent({
      name: UserMenuEvents.openAccountSettings,
      props: { from: "user-menu dropdown" },
    });
    handleClose();

    // Need to use redirectToAccountSettingsPage instead of navigateToAccountSettingsPage
    // because we need to preserve the query params in the URL attaching with settingsOriginUrl.
    // navigateToAccountSettingsPage users navigateWithPreservedParams which removes all params except PRESERVED_QUERY_PARAMS.
    redirectToAccountSettingsPage(tab);
  }

  // Trim version to remove everything after the dash
  const trimmedVersion = runtimeConfig.appVersion.split("-")[0];

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          // Let the user avatar determine the size
          [">:first-of-type"]: {
            width: "auto",
            height: "auto",
          },
        }}
      >
        <UserAvatar
          userDisplayInfo={userDisplayInfo}
          icon={userDisplayInfo ? undefined : <UserAvatarIcon />}
          shouldShowGradientBorder
          size="s"
        />
      </IconButton>

      <UserMenuDropdown
        anchorElement={menuAnchorElement}
        userDisplayInfo={userDisplayInfo}
        open={isMenuOpen}
        closeMenu={handleClose}
        appVersion={trimmedVersion}
        links={links}
        onOpenLegalDocuments={() => {
          trackEvent({ name: UserMenuEvents.openLegalDocuments });
        }}
        onSettingsClick={() =>
          handleAccountSettingsClick(AccountSettingsTabs.security)
        }
        onLogoutClick={() => {
          trackEvent({
            name: UserMenuEvents.logOut,
            props: {
              from: "user-menu dropdown",
            },
          });
          logout();
        }}
        onLoginClick={() => {
          trackEvent({
            name: UserMenuEvents.logIn,
            props: {
              from: "user-menu dropdown",
            },
          });
          navigateToRoot();
        }}
      />
    </>
  );
}
